import { Component, type ErrorInfo, type ReactNode } from "react";

// ── Types ──

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  showDetails: boolean;
}

// ── Error Boundary ──

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, showDetails: false };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("mojoReader: Uncaught rendering error", error, info.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, showDetails: false });
  };

  toggleDetails = () => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }));
  };

  render() {
    if (this.state.hasError) {
      const { error, showDetails } = this.state;

      return (
        <div style={styles.container}>
          <div style={styles.card}>
            {/* Logo / brand */}
            <div style={styles.logoRow}>
              <img
                src="/logo.png"
                alt="mojoReader"
                style={styles.logo}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
              <span style={styles.brand}>mojoReader</span>
            </div>

            {/* Heading */}
            <h1 style={styles.heading}>Something went wrong</h1>
            <p style={styles.subtitle}>
              An unexpected error prevented the page from rendering.
            </p>

            {/* Actions */}
            <div style={styles.actions}>
              <button onClick={this.handleReload} style={styles.primaryBtn}>
                Reload
              </button>
              <button onClick={this.handleReset} style={styles.secondaryBtn}>
                Try a different file
              </button>
            </div>

            {/* Expandable error details */}
            {error && (
              <div style={styles.detailsSection}>
                <button
                  onClick={this.toggleDetails}
                  style={styles.detailsToggle}
                  aria-expanded={showDetails}
                >
                  {showDetails ? "▾ Hide details" : "▸ Show details"}
                </button>
                {showDetails && (
                  <pre style={styles.errorPre}>
                    {error.name}: {error.message}
                    {error.stack && `\n\n${error.stack}`}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ── Inline styles (uses CSS custom properties for dark mode compat) ──

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    padding: "24px",
    background: "var(--background)",
    color: "var(--foreground)",
    fontFamily: "'Nunito', sans-serif",
  },
  card: {
    maxWidth: 480,
    width: "100%",
    background: "var(--card-bg)",
    border: "1px solid var(--card-border)",
    borderRadius: "var(--radius-lg)",
    padding: "40px 32px",
    textAlign: "center" as const,
    boxShadow: "var(--shadow-lg)",
  },
  logoRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    marginBottom: "24px",
  },
  logo: {
    width: 40,
    height: 40,
  },
  brand: {
    fontSize: 20,
    fontWeight: 700,
    color: "var(--foreground)",
  },
  heading: {
    fontSize: 22,
    fontWeight: 700,
    marginBottom: 8,
    color: "var(--foreground)",
  },
  subtitle: {
    fontSize: 14,
    color: "var(--muted-foreground)",
    marginBottom: 28,
    lineHeight: 1.5,
  },
  actions: {
    display: "flex",
    gap: "12px",
    justifyContent: "center",
    marginBottom: 24,
  },
  primaryBtn: {
    padding: "10px 24px",
    borderRadius: "var(--radius-sm)",
    border: "none",
    background: "var(--primary-color)",
    color: "var(--primary-foreground)",
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
    transition: "var(--transition-fast)",
    fontFamily: "inherit",
  },
  secondaryBtn: {
    padding: "10px 24px",
    borderRadius: "var(--radius-sm)",
    border: "1px solid var(--border)",
    background: "var(--secondary)",
    color: "var(--secondary-foreground)",
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
    transition: "var(--transition-fast)",
    fontFamily: "inherit",
  },
  detailsSection: {
    borderTop: "1px solid var(--border)",
    paddingTop: 16,
  },
  detailsToggle: {
    background: "none",
    border: "none",
    color: "var(--muted-foreground)",
    fontSize: 13,
    cursor: "pointer",
    padding: "4px 8px",
    fontFamily: "inherit",
  },
  errorPre: {
    marginTop: 12,
    padding: 16,
    background: "var(--muted)",
    borderRadius: "var(--radius-sm)",
    fontSize: 12,
    color: "var(--error)",
    textAlign: "left" as const,
    overflow: "auto",
    maxHeight: 200,
    whiteSpace: "pre-wrap" as const,
    wordBreak: "break-word" as const,
    fontFamily: "'Fira Code', 'Cascadia Code', monospace",
  },
};

export default ErrorBoundary;
